import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { motion } from 'framer-motion';
import { UtensilsCrossed, MessageCircle, Repeat2, Share, Sparkles, Lock, Volume2, VolumeX, BarChart3, MoreHorizontal, Trash2, Flag, ShieldAlert } from 'lucide-react';
import { usePostActions, REACTIONS, SKIN_TONES, getSkinTone, setSkinTone, getReactionEmoji } from '@/hooks/usePostActions';
import { ENERGIES, POST_BACKGROUNDS, CAMERA_FILTERS, ENERGY_DUOTONE } from '@/components/ComposerModal';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ComposerModal } from '@/components/ComposerModal';
import { ReportModal } from '@/components/ReportModal';
import { AccountabilityModal } from '@/components/AccountabilityModal';
import { LinkPreviewCard } from '@/components/TrendingWidget';
import { AttachedTrackCard } from '@/components/MusicPicker';
import { cn } from '@/lib/utils';
import { getTopBadge } from '@/lib/badges';
import { asPlainText, BONITA_FEED_PLACEHOLDER, isBonitaFailurePlainText } from '@/lib/safeText';

const fadeInUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: 'easeOut', delay },
  }),
};

const useTheme = () => {
  const [isDark, setIsDark] = useState(() => {
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.getAttribute('data-theme') === 'dark');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);
  return isDark;
};

export const FeedCard = ({ post }) => {
  const isDark = useTheme();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const cardRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reactionPickerOpen, setReactionPickerOpen] = useState(false);
  const [skinTone, setSkinToneState] = useState(() => getSkinTone());

  const {
    isValidPost,
    isOwner,
    isPlated,
    plateCount,
    pollData,
    votedIndex,
    voting,
    replyOpen,
    setReplyOpen,
    quoteOpen,
    setQuoteOpen,
    reportOpen,
    setReportOpen,
    accountabilityOpen,
    setAccountabilityOpen,
    isPraised,
    praiseCount,
    handlePraise,
    userReaction,
    reactionCounts,
    handlePlate,
    handleDelete,
    handleShare,
    handleVote,
    handleReact,
    goToPost,
  } = usePostActions(post);

  // Derived
  const postUser = isValidPost ? {
    name: post.user?.name || 'Anonymous',
    username: post.user?.username || 'user',
    picture: post.user?.picture || `https://api.dicebear.com/7.x/initials/svg?seed=${post.user?.name || 'U'}&backgroundColor=1a1a1a&textColor=ffffff`,
    user_id: post.user?.user_id || post.user_id,
  } : { name: 'Unknown', username: 'user', picture: '', user_id: '' };

  const isBonita = post?.is_spark || post?.user_id === 'bonita' || postUser.username === 'bonita';
  const rawBody = isValidPost ? asPlainText(post.content) : '';
  const bodyText =
    isValidPost && isBonita && isBonitaFailurePlainText(post.content)
      ? BONITA_FEED_PLACEHOLDER
      : rawBody;
  const isCookout = post?.visibility === 'cookout';
  const energyInfo = isValidPost && post.energy ? ENERGIES.find(e => e.key === post.energy) : null;

  // Map energy key → BLVX pill CSS class
  const ENERGY_PILL_MAP = {
    hot_take: 'ep-hot', real_talk: 'ep-real', confession: 'ep-con',
    the_plug: 'ep-plug', w: 'ep-w',
  };
  const energyPillClass = post?.energy ? ENERGY_PILL_MAP[post.energy] || '' : '';
  const hasMedia = post?.media_url;
  const isVideo = post?.media_type === 'video';

  // Visual enhancement derived state
  const bgInfo = post?.background ? POST_BACKGROUNDS.find(b => b.key === post.background) : null;
  const filterInfo = post?.filter ? CAMERA_FILTERS.find(f => f.key === post.filter) : null;
  const duotone = hasMedia && post?.energy ? ENERGY_DUOTONE[post.energy] : null;
  const overlayMode = hasMedia || !!bgInfo; // treat gradient bg same as media for text colors

  const formatTime = (dateStr) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: false });
    } catch {
      return '';
    }
  };

  const [isVisible, setIsVisible] = useState(false);

  // Auto-play video + track visibility for entrance animations
  useEffect(() => {
    if (!isValidPost || !cardRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (isVideo) videoRef.current?.play().catch(() => {});
          } else {
            if (isVideo) videoRef.current?.pause();
          }
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [isValidPost, isVideo]);

  const toggleMute = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  if (!isValidPost) return null;

  // Color classes
  const textClass = isDark ? 'text-white' : 'text-gray-900';
  const textMutedClass = isDark ? 'text-white/50' : 'text-gray-500';
  const textVeryMutedClass = isDark ? 'text-white/30' : 'text-gray-400';
  const borderClass = isDark ? 'border-white/10' : 'border-gray-200';

  return (
    <>
      <div
        ref={cardRef}
        className={cn(
          "feed-card relative flex flex-col",
          !bgInfo && !hasMedia && (isDark ? "bg-black" : "bg-white"),
          isBonita && "border-l-2 border-l-purple-500/50"
        )}
        style={bgInfo ? { background: bgInfo.css } : (!hasMedia ? { boxShadow: isDark ? '0 1px 0 rgba(255,255,255,0.05), 0 -1px 0 rgba(255,255,255,0.03)' : '0 1px 0 rgba(0,0,0,0.06), 0 -1px 0 rgba(0,0,0,0.03)' } : undefined)}
      >
        {/* Media background (if media post) */}
        {hasMedia && (
          <div className="absolute inset-0 z-0">
            {isVideo ? (
              <video
                ref={videoRef}
                src={post.media_url}
                loop
                muted
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
                style={filterInfo && filterInfo.css !== 'none' ? { filter: filterInfo.css } : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  if (videoRef.current?.paused) {
                    videoRef.current.play();
                  } else {
                    videoRef.current?.pause();
                  }
                }}
              />
            ) : (
              <img
                src={post.media_url}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
                style={filterInfo && filterInfo.css !== 'none' ? { filter: filterInfo.css } : undefined}
              />
            )}
            {/* Gradient overlay for readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />
            {/* Energy Duotone — unique color treatment per energy tag */}
            {duotone && !post.filter && (
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: `linear-gradient(135deg, ${duotone.from}, ${duotone.to})`,
                  mixBlendMode: 'color',
                  opacity: 0.55,
                }}
              />
            )}
            {/* Video mute toggle moved to action buttons column */}
          </div>
        )}

        {/* Energy emoji watermark — ambient vibe layer */}
        {energyInfo && (
          hasMedia ? (
            // Media/video: dramatic flash → shrinks to corner badge above username
            <>
              {/* Flash — centered, fades out after 2s */}
              <motion.div
                className="absolute inset-0 z-[5] pointer-events-none flex items-center justify-center overflow-hidden"
                initial={{ opacity: 0 }}
                animate={isVisible ? { opacity: [0, 0.7, 0] } : { opacity: 0 }}
                transition={{ duration: 1.8, ease: 'easeOut', times: [0, 0.2, 1], delay: 0.2 }}
              >
                <motion.span
                  className="drop-shadow-2xl select-none"
                  style={{ fontSize: '8rem' }}
                  animate={isVisible ? { scale: [3, 1.2, 0.5] } : { scale: 3 }}
                  transition={{ duration: 1.8, ease: 'easeOut', times: [0, 0.2, 1], delay: 0.2 }}
                >
                  {energyInfo.icon}
                </motion.span>
              </motion.div>
              {/* Corner badge — fades in after flash settles */}
              <motion.span
                className="absolute bottom-20 left-4 z-[6] pointer-events-none select-none drop-shadow-lg"
                style={{ fontSize: '2.5rem' }}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={isVisible ? { opacity: 0.55, scale: 1 } : { opacity: 0, scale: 0.5 }}
                transition={{ duration: 0.5, ease: 'easeOut', delay: 1.8 }}
              >
                {energyInfo.icon}
              </motion.span>
            </>
          ) : (
            // Text / gradient: large centered watermark
            <div className="absolute inset-0 z-[1] pointer-events-none flex items-center justify-center overflow-hidden">
              <span
                className="select-none"
                style={{ fontSize: '14rem', opacity: bgInfo ? 0.1 : 0.07 }}
              >
                {energyInfo.icon}
              </span>
            </div>
          )
        )}

        {/* Content area — positioned over media for media posts, centered for text posts */}
        <div className={cn(
          "relative z-10 flex-1 flex flex-col px-4 pb-4 min-h-0",
          overlayMode ? "justify-end pt-20" : "pt-4"
        )}>
          {/* Energy badge — top area (text-only posts without overlayMode) */}
          {energyInfo && !overlayMode && (
            <div className="absolute top-4 left-4">
              <span className={cn("ep", energyPillClass)}>
                <span>{energyInfo.icon}</span>
                <span className="font-display tracking-wider uppercase">{energyInfo.label}</span>
              </span>
            </div>
          )}

          {/* Cookout badge */}
          {isCookout && !overlayMode && (
            <div className="absolute top-4 right-4">
              <Lock className={cn("h-4 w-4", textMutedClass)} />
            </div>
          )}

          {/* Scrollable content zone — prevents long posts from clipping */}
          <div
            className={cn(
              "flex-1 min-h-0 flex flex-col",
              overlayMode ? "justify-end" : "justify-center overflow-y-auto"
            )}
            style={!overlayMode ? { scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' } : undefined}
          >
          {/* Main content — centered for text posts, bottom for media/bg posts */}
          <div className={cn(
            "max-w-lg",
            overlayMode ? "mb-2" : "mx-auto text-center"
          )}>
            {/* Energy badge for overlay posts */}
            {energyInfo && overlayMode && (
              <motion.span
                variants={fadeInUp}
                initial="hidden"
                animate={isVisible ? "visible" : "hidden"}
                custom={0}
                className={cn("ep", energyPillClass, "mb-3")}
              >
                <span>{energyInfo.icon}</span>
                <span className="font-display tracking-wider uppercase">{energyInfo.label}</span>
              </motion.span>
            )}

            {/* Reply context — shown when this post is a reply */}
            {post.parent_post_id && post.parent_post?.user && (
              <div className={cn("flex items-center gap-1.5 mb-1.5 text-xs", overlayMode ? "text-white/50" : textVeryMutedClass)}>
                <MessageCircle className="h-3 w-3" />
                <span>replying to <span className={overlayMode ? "text-white/70" : textMutedClass}>@{post.parent_post.user.username}</span></span>
              </div>
            )}

            {/* Post text */}
            <motion.p
              variants={fadeInUp}
              initial="hidden"
              animate={isVisible ? "visible" : "hidden"}
              custom={0.08}
              className={cn(
              "whitespace-pre-wrap break-words leading-relaxed",
              overlayMode
                ? "text-white text-base drop-shadow-lg"
                : cn(
                    textClass,
                    bodyText.length < 100 ? "text-2xl font-medium" :
                    bodyText.length < 250 ? "text-xl" : "text-base"
                  )
            )}>
              {bodyText}
            </motion.p>

            {/* Link preview */}
            {post.reference_url && !overlayMode && (
              <div className="mt-4 text-left" onClick={(e) => e.stopPropagation()}>
                <LinkPreviewCard url={post.reference_url} />
              </div>
            )}

            {/* Poll */}
            {pollData && (
              <div className={cn("mt-4 space-y-1.5", overlayMode ? "" : "text-left")} onClick={(e) => e.stopPropagation()}>
                {pollData.options.map((option, index) => {
                  const hasVoted = votedIndex !== null;
                  const totalVotes = pollData.total_votes || 0;
                  const pct = totalVotes > 0 ? Math.round((option.votes / totalVotes) * 100) : 0;
                  const isMyVote = votedIndex === index;

                  return hasVoted ? (
                    <div
                      key={index}
                      className={cn(
                        "relative border px-3 py-2 overflow-hidden",
                        overlayMode ? "border-white/20 bg-black/30 backdrop-blur-sm" :
                        isMyVote
                          ? isDark ? "border-amber-500/40" : "border-amber-500/50"
                          : isDark ? "border-white/10" : "border-gray-200"
                      )}
                    >
                      <div
                        className={cn(
                          "absolute inset-y-0 left-0 transition-all duration-500",
                          overlayMode ? "bg-white/15" :
                          isMyVote
                            ? isDark ? "bg-amber-500/15" : "bg-amber-500/10"
                            : isDark ? "bg-white/5" : "bg-gray-100"
                        )}
                        style={{ width: `${pct}%` }}
                      />
                      <div className="relative flex items-center justify-between">
                        <span className={cn("text-sm", overlayMode ? "text-white" : isMyVote ? (isDark ? "text-amber-400 font-medium" : "text-amber-600 font-medium") : textClass)}>
                          {option.text}
                        </span>
                        <span className={cn("text-xs font-mono ml-2", overlayMode ? "text-white/70" : textMutedClass)}>
                          {pct}%
                        </span>
                      </div>
                    </div>
                  ) : (
                    <button
                      key={index}
                      onClick={() => handleVote(index)}
                      disabled={voting}
                      className={cn(
                        "w-full text-left border px-3 py-2 text-sm transition-colors",
                        overlayMode
                          ? "border-white/20 bg-black/30 backdrop-blur-sm text-white hover:bg-white/10"
                          : isDark
                            ? "border-white/10 hover:border-white/25 hover:bg-white/5"
                            : "border-gray-200 hover:border-gray-400 hover:bg-gray-50",
                        !overlayMode && textClass,
                        voting && "opacity-50 cursor-wait"
                      )}
                    >
                      {option.text}
                    </button>
                  );
                })}
                <div className={cn("flex items-center gap-2 pt-1", overlayMode ? "text-white/50" : textVeryMutedClass)}>
                  <BarChart3 className="h-3 w-3" />
                  <span className="text-[11px]">
                    {pollData.total_votes} {pollData.total_votes === 1 ? 'vote' : 'votes'}
                  </span>
                </div>
              </div>
            )}

            {/* Reaction shelf — culturally-themed emoji strip */}
            {Object.keys(reactionCounts).length > 0 && (
              <motion.div
                variants={fadeInUp}
                initial="hidden"
                animate={isVisible ? "visible" : "hidden"}
                custom={0.16}
                className="flex items-center gap-2 mt-3 flex-wrap"
              >
                {REACTIONS.filter(r => reactionCounts[r.key] > 0).map(r => (
                  <motion.button
                    whileHover={{ scale: 1.15 }}
                    whileTap={{ scale: 0.9 }}
                    key={r.key}
                    onClick={(e) => { e.stopPropagation(); handleReact(r.key); }}
                    className={cn(
                      "flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-all",
                      overlayMode
                        ? userReaction === r.key
                          ? "bg-amber-500/30 border border-amber-400/60 text-amber-200"
                          : "bg-black/30 backdrop-blur-sm border border-white/20 text-white/80 hover:bg-amber-500/20"
                        : userReaction === r.key
                          ? "bg-amber-500/15 border border-amber-500/40 text-amber-400"
                          : isDark ? "bg-white/5 border border-white/10 text-white/60 hover:bg-amber-500/10 hover:border-amber-500/30" : "bg-gray-50 border border-gray-200 text-gray-500 hover:bg-amber-50 hover:border-amber-200"
                    )}
                  >
                    <span>{getReactionEmoji(r, skinTone)}</span>
                    <span className="font-mono">{reactionCounts[r.key]}</span>
                  </motion.button>
                ))}
              </motion.div>
            )}

          {/* Quoted post */}
            {post.quote_post && (
              <div
                className={cn(
                  "mt-3 border p-3 transition-colors cursor-pointer",
                  overlayMode
                    ? "border-white/20 bg-black/30 backdrop-blur-sm"
                    : cn(borderClass, isDark ? "hover:bg-white/5" : "hover:bg-gray-50")
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/post/${post.quote_post.post_id}`);
                }}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <Avatar className="h-5 w-5">
                    <AvatarImage src={post.quote_post.user?.picture} />
                    <AvatarFallback className="text-[10px]">{post.quote_post.user?.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <span className={cn("text-xs font-medium", overlayMode ? "text-white/80" : isDark ? "text-white/70" : "text-gray-700")}>
                    {asPlainText(post.quote_post.user?.name) || 'User'}
                  </span>
                </div>
                <p className={cn("text-sm line-clamp-2", overlayMode ? "text-white/70" : isDark ? "text-white/60" : "text-gray-600")}>
                  {asPlainText(post.quote_post.content)}
                </p>
              </div>
            )}
          </div>

          {/* Attached music track */}
          {post.music_track && (
            <div className="mt-3 px-1" onClick={(e) => e.stopPropagation()}>
              <AttachedTrackCard track={post.music_track} isDark={overlayMode ? true : isDark} />
            </div>
          )}
          </div>{/* end scrollable content zone */}

          {/* Bottom section: User info left + action buttons right */}
          <div className="flex items-end justify-between shrink-0 pt-2">
            {/* User info — bottom left (TikTok style) */}
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              animate={isVisible ? "visible" : "hidden"}
              custom={0.24}
            >
            <Link
              to={`/profile/${postUser.username}`}
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-2.5 group"
            >
              <Avatar className={cn("h-10 w-10 border-2", overlayMode ? "border-white/30" : borderClass)}>
                <AvatarImage src={postUser.picture} alt={postUser.name} />
                <AvatarFallback className={cn("text-sm font-medium", isDark ? "bg-white/10" : "bg-gray-100")}>
                  {postUser.name?.charAt(0)?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div>
                <div className="flex items-center gap-1.5">
                  <span className={cn(
                    "font-bold text-sm group-hover:underline",
                    overlayMode ? "text-white drop-shadow" : textClass
                  )}>
                    {postUser.name}
                  </span>
                  {isBonita && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-gradient-to-r from-purple-500/30 to-pink-500/30 border border-purple-500/40 text-[9px] font-medium text-purple-300 rounded-sm">
                      <Sparkles className="h-2 w-2" />
                      AI
                    </span>
                  )}
                  {(() => {
                    const badge = getTopBadge(postUser.active_badges);
                    return badge ? (
                      <span className={cn("inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-medium rounded-sm", badge.bg, badge.color)} title={badge.label}>
                        {badge.emoji} {badge.label}
                      </span>
                    ) : null;
                  })()}
                </div>
                <span className={cn("text-xs", overlayMode ? "text-white/60" : textMutedClass)}>
                  @{postUser.username} · {formatTime(post.created_at)}
                </span>
              </div>
            </Link>
            </motion.div>

            {/* Action buttons — right side vertical (TikTok style) */}
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              animate={isVisible ? "visible" : "hidden"}
              custom={0.32}
              className="flex flex-col items-center gap-4"
            >
              {/* Mute toggle — top of action column, always accessible */}
              {isVideo && (
                <button
                  onClick={toggleMute}
                  className="flex flex-col items-center gap-0.5"
                >
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center transition-colors",
                    overlayMode ? "bg-black/30 backdrop-blur-sm" : isDark ? "bg-white/5" : "bg-gray-100"
                  )}>
                    {isMuted
                      ? <VolumeX className="h-5 w-5 text-white" />
                      : <Volume2 className="h-5 w-5 text-white" />
                    }
                  </div>
                </button>
              )}

              {/* Plate */}
              <button
                onClick={handlePlate}
                className="flex flex-col items-center gap-0.5"
              >
                <motion.div
                  whileTap={{ scale: 0.85 }}
                  animate={isPlated ? { scale: [1, 1.2, 1] } : { scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center transition-colors",
                    overlayMode ? "bg-black/30 backdrop-blur-sm" : isDark ? "bg-white/5" : "bg-gray-100",
                    isPlated && "bg-amber-500/20"
                  )}
                >
                  <UtensilsCrossed className={cn(
                    "h-5 w-5",
                    isPlated ? "text-amber-500 fill-amber-500" : overlayMode ? "text-white" : textMutedClass
                  )} />
                </motion.div>
                {plateCount > 0 && (
                  <span className={cn("text-[11px] font-mono", overlayMode ? "text-white/80" : textMutedClass)}>
                    {plateCount}
                  </span>
                )}
              </button>

              {/* Praise — Cookout only */}
              {isCookout && !isOwner && (
                <button
                  onClick={handlePraise}
                  className="flex flex-col items-center gap-0.5"
                >
                  <motion.div
                    whileTap={{ scale: 0.85 }}
                    animate={isPraised ? { scale: [1, 1.2, 1] } : { scale: 1 }}
                    transition={{ duration: 0.3 }}
                    className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center transition-colors",
                      overlayMode ? "bg-black/30 backdrop-blur-sm" : isDark ? "bg-white/5" : "bg-gray-100",
                      isPraised && "bg-orange-500/20"
                    )}
                  >
                    <span className={cn("text-lg", isPraised ? "opacity-100" : "opacity-50")}>{'\u{1F373}'}</span>
                  </motion.div>
                  {praiseCount > 0 && (
                    <span className={cn("text-[11px] font-mono", overlayMode ? "text-white/80" : textMutedClass)}>
                      {praiseCount}
                    </span>
                  )}
                </button>
              )}

              {/* Reply */}
              <button
                onClick={(e) => { e.stopPropagation(); setReplyOpen(true); }}
                className="flex flex-col items-center gap-0.5"
              >
                <motion.div
                  whileTap={{ scale: 0.82 }}
                  whileHover={{ scale: 1.08 }}
                  transition={{ duration: 0.15 }}
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center transition-colors",
                    overlayMode ? "bg-black/30 backdrop-blur-sm" : isDark ? "bg-white/5" : "bg-gray-100"
                  )}
                >
                  <MessageCircle className={cn("h-5 w-5", overlayMode ? "text-white" : textMutedClass)} />
                </motion.div>
                {post.reply_count > 0 && (
                  <span className={cn("text-[11px] font-mono", overlayMode ? "text-white/80" : textMutedClass)}>
                    {post.reply_count}
                  </span>
                )}
              </button>

              {/* Repost/Quote */}
              <button
                onClick={(e) => { e.stopPropagation(); setQuoteOpen(true); }}
                className="flex flex-col items-center gap-0.5"
              >
                <motion.div
                  whileTap={{ scale: 0.82 }}
                  whileHover={{ scale: 1.08 }}
                  transition={{ duration: 0.15 }}
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center transition-colors",
                    overlayMode ? "bg-black/30 backdrop-blur-sm" : isDark ? "bg-white/5" : "bg-gray-100"
                  )}
                >
                  <Repeat2 className={cn("h-5 w-5", overlayMode ? "text-white" : textMutedClass)} />
                </motion.div>
                {post.repost_count > 0 && (
                  <span className={cn("text-[11px] font-mono", overlayMode ? "text-white/80" : textMutedClass)}>
                    {post.repost_count}
                  </span>
                )}
              </button>

              {/* Share */}
              <button
                onClick={(e) => { e.stopPropagation(); handleShare(); }}
                className="flex flex-col items-center"
              >
                <motion.div
                  whileTap={{ scale: 0.82 }}
                  whileHover={{ scale: 1.08 }}
                  transition={{ duration: 0.15 }}
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center transition-colors",
                    overlayMode ? "bg-black/30 backdrop-blur-sm" : isDark ? "bg-white/5" : "bg-gray-100"
                  )}
                >
                  <Share className={cn("h-5 w-5", overlayMode ? "text-white" : textMutedClass)} />
                </motion.div>
              </button>

              {/* React button — opens emoji picker */}
              <div className="relative">
                <button
                  onClick={(e) => { e.stopPropagation(); setReactionPickerOpen(!reactionPickerOpen); }}
                  className="flex flex-col items-center gap-0.5"
                >
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center transition-colors text-lg",
                    overlayMode ? "bg-black/30 backdrop-blur-sm" : isDark ? "bg-white/5" : "bg-gray-100",
                    userReaction && "ring-1 ring-amber-500/50"
                  )}>
                    {userReaction
                      ? getReactionEmoji(REACTIONS.find(r => r.key === userReaction), skinTone)
                      : getReactionEmoji(REACTIONS.find(r => r.key === 'real_talk'), skinTone)}
                  </div>
                </button>
                {reactionPickerOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setReactionPickerOpen(false)} />
                    <div className={cn(
                      "absolute bottom-12 right-0 z-50 border shadow-xl",
                      isDark ? "bg-zinc-900 border-white/10" : "bg-white border-gray-200"
                    )}>
                      {/* Skin tone row */}
                      <div className={cn("flex items-center gap-0.5 px-2 py-1.5 border-b", isDark ? "border-white/10" : "border-gray-100")}>
                        {SKIN_TONES.map(tone => (
                          <button
                            key={tone.key}
                            onClick={(e) => { e.stopPropagation(); setSkinTone(tone.modifier); setSkinToneState(tone.modifier); }}
                            className={cn(
                              "w-7 h-7 flex items-center justify-center rounded-full text-base transition-all hover:scale-110",
                              skinTone === tone.modifier && "bg-amber-500/20 ring-1 ring-amber-500/50"
                            )}
                            title={tone.key}
                          >
                            {'✊' + tone.modifier}
                          </button>
                        ))}
                      </div>
                      {/* Reaction row */}
                      <div className="flex items-center gap-1 px-2 py-1.5">
                        {REACTIONS.map(r => (
                          <motion.button
                            key={r.key}
                            title={r.label}
                            whileHover={{ scale: 1.25 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => { e.stopPropagation(); handleReact(r.key); setReactionPickerOpen(false); }}
                            className={cn(
                              "text-xl w-9 h-9 flex items-center justify-center rounded-full transition-colors",
                              userReaction === r.key && "bg-amber-500/20"
                            )}
                          >
                            {getReactionEmoji(r, skinTone)}
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* More menu */}
              <div className="relative">
                <button
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
                  className="flex flex-col items-center"
                >
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center transition-colors",
                    overlayMode ? "bg-black/30 backdrop-blur-sm" : isDark ? "bg-white/5" : "bg-gray-100"
                  )}>
                    <MoreHorizontal className={cn("h-5 w-5", overlayMode ? "text-white" : textMutedClass)} />
                  </div>
                </button>
                {menuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                    <div className={cn(
                      "absolute bottom-12 right-0 z-50 min-w-[140px] border py-1 shadow-xl",
                      isDark ? "bg-zinc-900 border-white/10" : "bg-white border-gray-200"
                    )}>
                      <button
                        onClick={() => { goToPost(); setMenuOpen(false); }}
                        className={cn("w-full text-left px-3 py-2 text-xs", isDark ? "text-white/70 hover:bg-white/5" : "text-gray-600 hover:bg-gray-50")}
                      >
                        View thread
                      </button>
                      {isOwner && (
                        <button
                          onClick={() => { handleDelete(); setMenuOpen(false); }}
                          className="w-full text-left px-3 py-2 text-xs text-red-500 hover:bg-red-500/10 flex items-center gap-2"
                        >
                          <Trash2 className="h-3 w-3" />Delete
                        </button>
                      )}
                      {!isOwner && isCookout && (
                        <button
                          onClick={() => { setAccountabilityOpen(true); setMenuOpen(false); }}
                          className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 text-red-400 hover:bg-red-500/10"
                        >
                          <ShieldAlert className="h-3 w-3" />Come Get Your Mans
                        </button>
                      )}
                      {!isOwner && (
                        <button
                          onClick={() => { setReportOpen(true); setMenuOpen(false); }}
                          className={cn("w-full text-left px-3 py-2 text-xs flex items-center gap-2", isDark ? "text-white/70 hover:bg-white/5" : "text-gray-600 hover:bg-gray-50")}
                        >
                          <Flag className="h-3 w-3" />Report
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Tap to view thread — centered bottom indicator */}
        <div
          className="absolute bottom-0 left-0 right-0 z-10 flex justify-center pb-1 cursor-pointer"
          onClick={goToPost}
        >
          <span className={cn("text-[10px] font-display tracking-widest uppercase", overlayMode ? "text-white/30" : textVeryMutedClass)}>
            tap for thread
          </span>
        </div>
      </div>

      {/* Modals */}
      <ComposerModal open={replyOpen} onOpenChange={setReplyOpen} replyTo={post} />
      <ComposerModal open={quoteOpen} onOpenChange={setQuoteOpen} quotedPost={post} />
      <ReportModal open={reportOpen} onOpenChange={setReportOpen} targetType="post" targetId={post.post_id} targetName={`@${postUser.username}'s post`} />
      <AccountabilityModal open={accountabilityOpen} onOpenChange={setAccountabilityOpen} targetUserId={post.user_id} targetPostId={post.post_id} targetName={`@${postUser.username}`} />
    </>
  );
};
